import React, { createContext, useContext, useReducer, useState } from 'react';
import { RoleLevelEnum } from '@common/graphql';
import { Moment } from 'moment';
import { BillingCardEmploymentTypeRatesType } from '@institutions/graphql';

export type RateCardRoleType = {
  id: string;
  name: string;
  abbreviation: string;
};

export type RateCardAttributesType = {
  roleLevel?: RoleLevelEnum;
  role?: RateCardRoleType;
  effectiveDate?: Moment;
  pendingStaffingRequestsCount?: number;
  openStaffingRequestsCount?: number;
  pendingOffersCount?: number;
  extensionOffersCount?: number;
} & BillingCardEmploymentTypeRatesType;

type DeviationsType = {
  pendingStaffingRequests: string[];
  openStaffingRequests: string[];
  pendingOffers: string[];
  extensionOffers: string[];
};

export type SelectedOrganizationUnitType = {
  id: string;
  businessId: string;
  clinicalId: string;
};

type InitialValuesType = {
  selectedOrganizationUnits: SelectedOrganizationUnitType[];
  rateCardAttributes: RateCardAttributesType;
  deviations: DeviationsType;
};

const initialValues: InitialValuesType = {
  selectedOrganizationUnits: [],
  rateCardAttributes: {
    roleLevel: undefined,
    role: undefined,
    effectiveDate: undefined,
    travelerRate: {
      dayBillingRate: 0,
      eveningBillingRate: 0,
      nightBillingRate: 0,
      variableBillingRate: 0,
    },
    localRate: {
      dayBillingRate: 0,
      eveningBillingRate: 0,
      nightBillingRate: 0,
      variableBillingRate: 0,
    },
    directSourcingRate: {
      dayBillingRate: 0,
      eveningBillingRate: 0,
      nightBillingRate: 0,
      variableBillingRate: 0,
    },
    pendingStaffingRequestsCount: 0,
    openStaffingRequestsCount: 0,
    pendingOffersCount: 0,
    extensionOffersCount: 0,
  },
  deviations: {
    pendingStaffingRequests: [],
    openStaffingRequests: [],
    pendingOffers: [],
    extensionOffers: [],
  },
};

type ContextValueType = {
  rateCardAttributes: RateCardAttributesType;
  selectedOrganizationUnits: SelectedOrganizationUnitType[];
  deviations: DeviationsType;
  setRateCardAttributes: React.Dispatch<
    React.SetStateAction<RateCardAttributesType>
  >;
  setSelectedOrganizationUnits: React.Dispatch<
    React.SetStateAction<SelectedOrganizationUnitType[]>
  >;
  deviationsDispatchers: DeviationsDispatchers;
};

const LongTermRateCardCreationContext = createContext<
  ContextValueType | undefined
>(undefined);

const actionTypes = {
  setDeviations: 'setDeviations',
  deviation: 'deviation',
  undeviation: 'undeviation',
} as const;

type DeviationType = keyof DeviationsType;

type DeviationAction =
  | { type: 'setDeviations'; deviationType: DeviationType; payload: string[] }
  | { type: 'deviation'; deviationType: DeviationType; payload: string }
  | { type: 'undeviation'; deviationType: DeviationType; payload: string };

type DeviationsDispatchers = {
  pendingOffers: {
    setAll: (ids: string[]) => void;
    deviate: (id: string) => void;
    undeviate: (id: string) => void;
  };
  pendingStaffingRequests: {
    setAll: (ids: string[]) => void;
    deviate: (id: string) => void;
    undeviate: (id: string) => void;
  };
  openStaffingRequests: {
    setAll: (ids: string[]) => void;
    deviate: (id: string) => void;
    undeviate: (id: string) => void;
  };
  extensionOffers: {
    setAll: (ids: string[]) => void;
    deviate: (id: string) => void;
    undeviate: (id: string) => void;
  };
};

function undeviate(state: string[], uncheckedId: string): string[] {
  const stateIndex = state.findIndex((item) => item === uncheckedId);
  return [...state.slice(0, stateIndex), ...state.slice(stateIndex + 1)];
}

function deviate(state: string[], checkedId: string): string[] {
  return [...state, checkedId];
}

function deviationsReducer(
  state: DeviationsType,
  action: DeviationAction
): DeviationsType {
  switch (action.type) {
    case actionTypes.setDeviations:
      return { ...state, [action.deviationType]: action.payload };
    case actionTypes.deviation:
      return {
        ...state,
        [action.deviationType]: deviate(
          state[action.deviationType],
          action.payload
        ),
      };
    case actionTypes.undeviation:
      return {
        ...state,
        [action.deviationType]: undeviate(
          state[action.deviationType],
          action.payload
        ),
      };
    default:
      throw new Error('Unsupported type');
  }
}

function useDeviations(
  initialValue: DeviationsType
): [DeviationsType, DeviationsDispatchers] {
  const [deviations, dispatch] = useReducer(deviationsReducer, initialValue);
  const { setDeviations, deviation, undeviation } = actionTypes;

  function dispatchSetAll(deviationType: DeviationType, payload: string[]) {
    return dispatch({ type: setDeviations, deviationType, payload });
  }

  function dispatchDeviation(deviationType: DeviationType, payload: string) {
    return dispatch({ type: deviation, deviationType, payload });
  }

  function dispatchUndeviation(deviationType: DeviationType, payload: string) {
    return dispatch({ type: undeviation, deviationType, payload });
  }

  function dispatchers(deviationType: DeviationType) {
    return {
      setAll: (ids: string[]) => dispatchSetAll(deviationType, ids),
      deviate: (id: string) => dispatchDeviation(deviationType, id),
      undeviate: (id: string) => dispatchUndeviation(deviationType, id),
    };
  }

  const deviationsDispatchers: DeviationsDispatchers = {
    pendingOffers: dispatchers('pendingOffers'),
    pendingStaffingRequests: dispatchers('pendingStaffingRequests'),
    openStaffingRequests: dispatchers('openStaffingRequests'),
    extensionOffers: dispatchers('extensionOffers'),
  };

  return [deviations, deviationsDispatchers];
}

export function RatesLongTermStepsProvider({
  children,
  initialState = initialValues,
}: {
  children: React.ReactNode;
  initialState?: InitialValuesType;
}) {
  const [rateCardAttributes, setRateCardAttributes] =
    useState<RateCardAttributesType>(initialState.rateCardAttributes);
  const [selectedOrganizationUnits, setSelectedOrganizationUnits] = useState<
    SelectedOrganizationUnitType[]
  >(initialState.selectedOrganizationUnits);

  const [deviations, deviationsDispatchers] = useDeviations(
    initialState.deviations
  );

  const state = {
    rateCardAttributes,
    selectedOrganizationUnits,
    deviations,
  };

  const dispatchers = {
    setRateCardAttributes,
    setSelectedOrganizationUnits,
    deviationsDispatchers,
  };

  return (
    <LongTermRateCardCreationContext.Provider
      value={{ ...state, ...dispatchers }}
    >
      {children}
    </LongTermRateCardCreationContext.Provider>
  );
}

export function useRateCardCreationContext(): ContextValueType {
  const context = useContext(LongTermRateCardCreationContext);
  if (!context) {
    throw new Error(
      'useRateCardCreationContext must be used within a RatesLongTermStepsProvider'
    );
  }

  return context;
}

export function useSelectedOrganizationUnits(): [
  SelectedOrganizationUnitType[],
  React.Dispatch<React.SetStateAction<SelectedOrganizationUnitType[]>>,
] {
  const { selectedOrganizationUnits, setSelectedOrganizationUnits } =
    useRateCardCreationContext();

  return [selectedOrganizationUnits, setSelectedOrganizationUnits];
}

export function useRateCardCreationAttributes(): [
  RateCardAttributesType,
  React.Dispatch<React.SetStateAction<RateCardAttributesType>>,
] {
  const { rateCardAttributes, setRateCardAttributes } =
    useRateCardCreationContext();
  return [rateCardAttributes, setRateCardAttributes];
}

export function useRateCardDeviations(): [
  DeviationsType,
  DeviationsDispatchers,
] {
  const { deviations, deviationsDispatchers } = useRateCardCreationContext();
  return [deviations, deviationsDispatchers];
}

export function usePendingOffersDeviation(): [
  string[],
  DeviationsDispatchers['pendingOffers'],
] {
  const { deviations, deviationsDispatchers } = useRateCardCreationContext();
  return [deviations.pendingOffers, deviationsDispatchers.pendingOffers];
}

export function usePendingStaffingRequestsDeviation(): [
  string[],
  DeviationsDispatchers['pendingStaffingRequests'],
] {
  const { deviations, deviationsDispatchers } = useRateCardCreationContext();
  return [
    deviations.pendingStaffingRequests,
    deviationsDispatchers.pendingStaffingRequests,
  ];
}

export function useExtensionOffersDeviation(): [
  string[],
  DeviationsDispatchers['extensionOffers'],
] {
  const { deviations, deviationsDispatchers } = useRateCardCreationContext();
  return [deviations.extensionOffers, deviationsDispatchers.extensionOffers];
}

export function useOpenStaffingRequestsDeviation(): [
  string[],
  DeviationsDispatchers['openStaffingRequests'],
] {
  const { deviations, deviationsDispatchers } = useRateCardCreationContext();
  return [
    deviations.openStaffingRequests,
    deviationsDispatchers.openStaffingRequests,
  ];
}

export function withRateCardCreationContext(Component: React.ComponentType) {
  return (props: React.ComponentProps<typeof Component>) => (
    <RatesLongTermStepsProvider>
      <Component {...props} />
    </RatesLongTermStepsProvider>
  );
}
